import {
  createFileRoute,
} from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  getShareSession,
  resolveShareInvitation,
  sendShareOtp,
  verifyShareOtp,
} from "@/app/api/share";
import type { ResolveShareInvitationResponse } from "@/app/api/share/types";

import { Button } from "@/components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LabeledInput } from "@/components/ui/input/LabeledInput";
import { SharePatientWorkspace } from "@/components/share/SharePatientWorkspace";

export const Route = createFileRoute("/share/")({
  component: ShareAccessEntryPage,
});

type InvitationState =
  | "capturing"
  | "loading"
  | "ready"
  | "code_sent"
  | "validating_session"
  | "authenticated"
  | "access_ended"
  | "session_error"
  | "unavailable"
  | "error";

const SHARE_INVITATION_TOKEN_PATTERN =
  /^[A-Za-z0-9_-]{43}$/;

const SHARE_OTP_PATTERN = /^\d{6}$/;

const SHARE_ACCESS_TOKEN_STORAGE_KEY =
  "mimiat_share_access_token";

const SHARE_OTP_RESEND_COOLDOWN_SECONDS = 60;

const readAndScrubInvitationToken = (): string | null => {
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;

  const params = new URLSearchParams(hash);
  const token = params.get("token")?.trim() ?? "";

  window.history.replaceState(
    window.history.state,
    document.title,
    `${window.location.pathname}${window.location.search}`,
  );

  return SHARE_INVITATION_TOKEN_PATTERN.test(token)
    ? token
    : null;
};

function ShareAccessEntryPage() {
  const { t, i18n } = useTranslation();

  const captureStartedRef = useRef(false);

  const [invitationToken, setInvitationToken] =
    useState<string | null>(null);

  const [invitation, setInvitation] =
    useState<ResolveShareInvitationResponse["data"] | null>(
      null,
    );

  const [state, setState] =
    useState<InvitationState>("capturing");

  const [resolveAttempt, setResolveAttempt] =
    useState(0);

  const [otp, setOtp] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] =
    useState<string | null>(null);

  const [shareAccessToken, setShareAccessToken] =
    useState<string | null>(null);

  const [shareAccessExpiresAt, setShareAccessExpiresAt] =
    useState<string | null>(null);

  const [isSendingCode, setIsSendingCode] =
    useState(false);

  const [isVerifyingCode, setIsVerifyingCode] =
    useState(false);

  const [sendCodeError, setSendCodeError] =
    useState(false);

  const [verifyCodeError, setVerifyCodeError] =
    useState(false);

  const [resendAvailableAt, setResendAvailableAt] =
    useState<number | null>(null);

  const [resendSeconds, setResendSeconds] =
    useState(0);

  useEffect(() => {
    /*
     * React StrictMode re-runs effects during development.
     * This prevents a second pass from reading a fragment
     * that has already been scrubbed.
     */
    if (captureStartedRef.current) {
      return;
    }

    captureStartedRef.current = true;

    const token = readAndScrubInvitationToken();

    if (token) {
      /*
       * A newly opened invitation starts a new Share lane.
       * Never allow a previous guest credential to bleed
       * into a new invitation flow.
       */
      try {
        sessionStorage.removeItem(
          SHARE_ACCESS_TOKEN_STORAGE_KEY,
        );
      } catch {
        /*
         * Browser storage may be unavailable. The invitation
         * flow itself must remain usable without persistence.
         */
      }

      setInvitationToken(token);
      return;
    }

    /*
     * No invitation fragment: this may be a refresh of an
     * already authenticated Share session. Storage is only
     * a candidate credential; the API remains authoritative.
     */
    try {
      const storedAccessToken =
        sessionStorage.getItem(
          SHARE_ACCESS_TOKEN_STORAGE_KEY,
        );

      if (
        storedAccessToken &&
        SHARE_INVITATION_TOKEN_PATTERN.test(
          storedAccessToken,
        )
      ) {
        setShareAccessToken(
          storedAccessToken,
        );
        setState("validating_session");
        return;
      }
    } catch {
      /*
       * Persistence is optional. Without an invitation or a
       * readable stored credential, access cannot be restored.
       */
    }

    setState("unavailable");
  }, []);

  useEffect(() => {
    if (!invitationToken) {
      return;
    }

    setState("loading");

    void resolveShareInvitation(invitationToken)
      .then((response) => {
        if (response.status === "fail") {
          setInvitation(null);
          setState("unavailable");
          return;
        }

        setInvitation(response.data);
        setState("ready");
      })
      .catch(() => {
        setInvitation(null);
        setState("error");
      });
  }, [invitationToken, resolveAttempt]);

  const [sessionAttempt, setSessionAttempt] =
    useState(0);

  const clearShareCredential = useCallback(() => {
    setShareAccessToken(null);
    setShareAccessExpiresAt(null);

    try {
      sessionStorage.removeItem(
        SHARE_ACCESS_TOKEN_STORAGE_KEY,
      );
    } catch {
      /*
       * In-memory access is still cleared even when browser
       * storage is unavailable.
       */
    }
  }, []);

  useEffect(() => {
    if (!shareAccessToken) {
      return;
    }

    let cancelled = false;

    setState("validating_session");

    void getShareSession(shareAccessToken)
      .then((response) => {
        if (cancelled) {
          return;
        }

        if (response.kind === "denied") {
          clearShareCredential();
          setState("access_ended");
          return;
        }

        if (response.kind === "error") {
          setState("session_error");
          return;
        }

        if (response.data.readOnly !== true) {
          clearShareCredential();
          setState("access_ended");
          return;
        }

        setShareAccessExpiresAt(
          response.data.expiresAt,
        );

        setState("authenticated");
      })
      .catch(() => {
        if (!cancelled) {
          setState("session_error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    shareAccessToken,
    sessionAttempt,
    clearShareCredential,
  ]);

  const handleAccessEnded = useCallback(() => {
    clearShareCredential();
    setState("access_ended");
  }, [clearShareCredential]);

  useEffect(() => {
    if (!resendAvailableAt) {
      setResendSeconds(0);
      return;
    }

    const updateRemaining = () => {
      const remaining = Math.max(
        0,
        Math.ceil(
          (resendAvailableAt - Date.now()) / 1000,
        ),
      );

      setResendSeconds(remaining);

      if (remaining === 0) {
        setResendAvailableAt(null);
      }
    };

    updateRemaining();

    const interval = window.setInterval(
      updateRemaining,
      1000,
    );

    return () => {
      window.clearInterval(interval);
    };
  }, [resendAvailableAt]);

  const formatDateTime = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));

  const formattedInvitationExpiry = invitation
    ? formatDateTime(invitation.invitationExpiresAt)
    : "";

  const formattedOtpExpiry = otpExpiresAt
    ? formatDateTime(otpExpiresAt)
    : "";

  const handleSendCode = async () => {
    if (!invitationToken || isSendingCode) {
      return;
    }

    setIsSendingCode(true);
    setSendCodeError(false);
    setVerifyCodeError(false);

    try {
      const response =
        await sendShareOtp(invitationToken);

      if (response.status === "fail") {
        setSendCodeError(true);
        return;
      }

      setOtp("");
      setOtpExpiresAt(response.data.otpExpiresAt);

      setResendAvailableAt(
        Date.now() +
          SHARE_OTP_RESEND_COOLDOWN_SECONDS * 1000,
      );

      setState("code_sent");
    } catch {
      setSendCodeError(true);
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    if (
      !invitationToken ||
      !SHARE_OTP_PATTERN.test(otp) ||
      isVerifyingCode
    ) {
      setVerifyCodeError(true);
      return;
    }

    setIsVerifyingCode(true);
    setVerifyCodeError(false);

    try {
      const response =
        await verifyShareOtp(
          invitationToken,
          otp,
        );

      if (response.status === "fail") {
        setVerifyCodeError(true);
        return;
      }

      /*
       * Authentication has already succeeded server-side at
       * this point, so retain the credential in memory first.
       * Browser storage is optional persistence for the
       * authenticated Share workspace and must never turn a
       * successful OTP verification into failure.
       */
      setShareAccessToken(
        response.data.accessToken,
      );
      setShareAccessExpiresAt(
        response.data.expiresAt,
      );

      try {
        sessionStorage.setItem(
          SHARE_ACCESS_TOKEN_STORAGE_KEY,
          response.data.accessToken,
        );
      } catch {
        /*
         * Continue safely in the current tab with the in-memory
         * credential. Persistence is never required for access.
         */
      }

      /*
       * OTP verification consumes the emailed invitation.
       * Drop our in-memory copy immediately after activation.
       */
      setInvitationToken(null);
      setOtp("");
      setState("validating_session");
    } catch {
      setVerifyCodeError(true);
    } finally {
      setIsVerifyingCode(false);
    }
  };

  if (
    state === "capturing" ||
    state === "loading" ||
    state === "validating_session"
  ) {
    return (
      <div
        id="share-access-workspace"
        className="max-w-xl mx-auto pt-16"
      >
        <Card className="bg-white">
          <CardContent
            className="py-10 text-center text-neutral-600"
            aria-live="polite"
          >
            {t("share_access.loading")}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "unavailable") {
    return (
      <div
        id="share-access-workspace"
        className="max-w-xl mx-auto pt-16"
      >
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-xl">
              {t(
                "share_access.unavailable_title",
              )}
            </CardTitle>
          </CardHeader>

          <CardContent>
            <p className="text-sm leading-6 text-neutral-600">
              {t(
                "share_access.unavailable_body",
              )}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div
        id="share-access-workspace"
        className="max-w-xl mx-auto pt-16"
      >
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-xl">
              {t("share_access.error_title")}
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-5">
            <p className="text-sm leading-6 text-neutral-600">
              {t("share_access.error_body")}
            </p>

            <Button
              type="button"
              onClick={() =>
                setResolveAttempt(
                  (attempt) => attempt + 1,
                )
              }
            >
              {t("share_access.retry")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "session_error") {
    return (
      <div
        id="share-access-workspace"
        className="max-w-xl mx-auto pt-16"
      >
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-xl">
              {t("share_access.session_error_title")}
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-5">
            <p className="text-sm leading-6 text-neutral-600">
              {t("share_access.session_error_body")}
            </p>

            <Button
              type="button"
              onClick={() =>
                setSessionAttempt(
                  (attempt) => attempt + 1,
                )
              }
            >
              {t("share_access.retry")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "access_ended") {
    return (
      <div
        id="share-access-workspace"
        className="max-w-xl mx-auto pt-16"
      >
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-xl">
              {t("share_access.access_ended_title")}
            </CardTitle>
          </CardHeader>

          <CardContent>
            <p className="text-sm leading-6 text-neutral-600">
              {t("share_access.access_ended_body")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (
    state === "authenticated" &&
    shareAccessToken &&
    shareAccessExpiresAt
  ) {
    return (
      <SharePatientWorkspace
        accessToken={shareAccessToken}
        expiresAt={shareAccessExpiresAt}
        onAccessEnded={handleAccessEnded}
      />
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <div
      id="share-access-workspace"
      className="max-w-xl mx-auto pt-10"
    >
      <Card className="bg-white">
        <CardHeader>
          <p className="text-sm font-medium text-[#B51F2A]">
            {t("share_access.eyebrow")}
          </p>

          <CardTitle className="text-2xl leading-tight">
            {state === "code_sent"
              ? t("share_access.code_sent_title")
              : t("share_access.title")}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {state === "ready" && (
            <>
              <div className="space-y-2">
                <p className="text-sm text-neutral-600">
                  {t("share_access.sent_to")}
                </p>

                <p className="font-semibold text-neutral-900">
                  {
                    invitation.recipientEmailMasked
                  }
                </p>
              </div>

              <p className="text-sm leading-6 text-neutral-700">
                {t("share_access.safety")}
              </p>

              <p className="text-sm text-neutral-500">
                {t("share_access.expires", {
                  date:
                    formattedInvitationExpiry,
                })}
              </p>

              <Button
                type="button"
                className="w-full"
                onClick={handleSendCode}
                isLoading={isSendingCode}
                loadingText={t(
                  "share_access.sending_code",
                )}
              >
                {t("share_access.send_code")}
              </Button>

              {sendCodeError && (
                <p
                  className="text-sm text-red-600"
                  role="alert"
                >
                  {t(
                    "share_access.send_error",
                  )}
                </p>
              )}

              <p className="text-xs leading-5 text-neutral-500">
                {t("share_access.notice")}
              </p>
            </>
          )}

          {state === "code_sent" && (
            <>
              <div className="space-y-2">
                <p className="text-sm leading-6 text-neutral-700">
                  {t(
                    "share_access.code_sent_body",
                    {
                      email:
                        invitation.recipientEmailMasked,
                    },
                  )}
                </p>

                {otpExpiresAt && (
                  <p className="text-sm text-neutral-500">
                    {t(
                      "share_access.code_expires",
                      {
                        date:
                          formattedOtpExpiry,
                      },
                    )}
                  </p>
                )}
              </div>

              <form
                onSubmit={handleVerifyCode}
                className="space-y-5"
              >
                <LabeledInput
                  id="share-verification-code"
                  label={t(
                    "share_access.code_label",
                  )}
                  required={true}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder={t(
                    "share_access.code_placeholder",
                  )}
                  value={otp}
                  onChange={(event) => {
                    const nextValue =
                      event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);

                    setOtp(nextValue);
                    setVerifyCodeError(false);
                  }}
                />

                {verifyCodeError && (
                  <p
                    className="text-sm text-red-600"
                    role="alert"
                  >
                    {t(
                      "share_access.verify_error",
                    )}
                  </p>
                )}

                {sendCodeError && (
                  <p
                    className="text-sm text-red-600"
                    role="alert"
                  >
                    {t(
                      "share_access.send_error",
                    )}
                  </p>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={
                    !SHARE_OTP_PATTERN.test(
                      otp,
                    )
                  }
                  isLoading={isVerifyingCode}
                  loadingText={t(
                    "share_access.verifying_code",
                  )}
                >
                  {t(
                    "share_access.verify_code",
                  )}
                </Button>
              </form>

              <div className="flex justify-center">
                <Button
                  type="button"
                  variant="link"
                  disabled={
                    resendSeconds > 0 ||
                    isSendingCode ||
                    isVerifyingCode
                  }
                  onClick={handleSendCode}
                >
                  {resendSeconds > 0
                    ? t(
                        "share_access.resend_in",
                        {
                          seconds:
                            resendSeconds,
                        },
                      )
                    : t(
                        "share_access.resend_code",
                      )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
